/**
 *
 * Displays a stacked report of all eurocode controls (RC, steel, timber)
 * from selProject.data.data.output.{rc,steel,timber}[].controls.
 */
import React from "react";
import { Accordion, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../app/hooks";
import { selectProject } from "../features/projects/projectSlice";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import type { NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { ng } from "@common/ngds";

interface ControlsReportProps {
  selLC?: string;
  onSelLCChange?: (value: string) => void;
}

interface GroupedControlsItem {
  title: string;
  eurocodeRef: string | null;
  controls: ng.result.control.ControlData_json[];
}

interface MaxControlCandidate {
  control: ng.result.control.ControlData_json;
  lcombType: string | undefined;
}

/** The pane of the report a control is shown in. */
export type ControlPane = "uls" | "sls" | "fire";

export const isFireType = (type: string | undefined) => type === "TYPE_FIRE";
export const isSlsType = (type: string | undefined) =>
  type === "TYPE_CHARACTERISTIC" || type === "TYPE_FREQUENT" || type === "TYPE_QUASI_PERMANENT";

export const paneForLcombType = (type: string | undefined): ControlPane =>
  isFireType(type) ? "fire" : isSlsType(type) ? "sls" : "uls";

/**
 * Identity of a control when taking the worst across load combinations: its TYPE plus the pane it
 * belongs to — never its title.
 *
 * One check can report several titles for the same type. RC-Design titles the biaxial moment check
 * `control_type_concrete_moment_biaxial_not_required_` at 0 % for a combination that EN 1992-1-1
 * 5.8.9 (3) screens out and `control_type_concrete_moment_biaxial_` where it is performed; keyed on
 * the title those become two entries, "Biaxial moment check not required" sitting next to a real
 * utilisation for the same element. Keyed on the type the higher utilisation wins, so the performed
 * check is what shows, and where every combination was screened out the note is the only candidate
 * and still comes through — which is the point of reporting it.
 *
 * The pane is part of the key because the other title split is fire vs non-fire, and those are two
 * verdicts on the same clause rather than alternatives: both are worth showing. Two ULS
 * combinations, or a FREQUENT and a QUASI_PERMANENT one, do compete for a single entry.
 *
 * Falls back to the title for a control carrying no type at all, so untyped controls cannot all
 * collapse into one entry.
 */
export const controlIdentityKey = (
  control: ng.result.control.ControlData_json,
  lcombType: string | undefined,
): string => {
  const typeFields = [control.concreteType, control.steelType, control.timberType, control.foundationType];
  const hasType = typeFields.some((field) => Boolean(field));
  const identity = hasType ? typeFields.map((field) => field ?? "").join("::") : (control.title ?? "");

  return [paneForLcombType(lcombType), identity].join("::");
};

export const ControlsReport: React.FC<ControlsReportProps> = ({ selLC, onSelLCChange }) => {
  const { t } = useTranslation();
  const selProject = useAppSelector(selectProject);
  const [expandedUlsControls, setExpandedUlsControls] = React.useState<Record<number, boolean>>({});
  const [expandedSlsControls, setExpandedSlsControls] = React.useState<Record<number, boolean>>({});
  const [expandedFireControls, setExpandedFireControls] = React.useState<Record<number, boolean>>({});
  const [expandedLcControls, setExpandedLcControls] = React.useState<Record<number, boolean>>({});
  const [activeTab, setActiveTab] = React.useState<string>("max");
  // Tracks the results object last seen so a fresh code check can be detected.
  const lastResultsRef = React.useRef(selProject?.data?.data?.output);

  const rcSummaries: ng.result.concrete.DesignSummary_json[] = selProject?.data?.data?.output?.rc ?? [];
  const steelSummaries: ng.result.steel.DesignSummary_json[] = selProject?.data?.data?.output?.steel ?? [];
  const timberSummaries: ng.result.timber.DesignSummary_json[] = selProject?.data?.data?.output?.timber ?? [];

  // Legacy fallback: flatten controls from DesignSummary entries.
  const summaryMaxControls: ng.result.control.ControlData_json[] = [
    ...rcSummaries.flatMap((ds) => ds.controls ?? []),
    ...steelSummaries.flatMap((ds) => ds.controls ?? []),
    ...timberSummaries.flatMap((ds) => ds.controls ?? []),
  ];

  // Controls for the currently selected load combination
  const lcombs: ng.output.LoadCombinationOutput_json[] = selProject?.data?.data?.output?.lcombs ?? [];
  const inputLoadCombinations: ng.loadcombination.Data_json[] =
    selProject?.data?.data?.input?.structures?.[0]?.loading?.combinations ?? [];

  // Build lookup maps from the input load combinations.
  const lcNameToType = React.useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const lc of inputLoadCombinations) {
      const name = lc?.id?.name;
      if (name && lc.type) {
        map.set(name, lc.type);
      }
    }
    return map;
  }, [inputLoadCombinations]);

  const lcombGuidToType = React.useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const lc of inputLoadCombinations) {
      const guid = lc?.id?.guid;
      if (guid && lc.type) {
        map.set(guid, lc.type);
      }
    }
    return map;
  }, [inputLoadCombinations]);

  const maxControlCandidatesFromLcombs = React.useMemo<MaxControlCandidate[]>(() => {
    const maxByControlKey = new Map<string, MaxControlCandidate>();

    const getUtilization = (control: ng.result.control.ControlData_json): number => {
      const value = Number(control.utilization);
      return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
    };

    for (const lcomb of lcombs) {
      const lcombGuid = lcomb.lcombGuid ?? "";
      const lcombType = lcombGuidToType.get(lcombGuid);

      for (const elRes of lcomb.elementResults ?? []) {
        const result = elRes.result?.result;
        if (!result) {
          continue;
        }

        const controls: ng.result.control.ControlData_json[] = [
          ...(result.rc?.maxOfControls ?? []),
          ...(result.steel?.maxOfControls ?? []),
          ...(result.timber?.maxOfControls ?? []),
        ];

        for (const control of controls) {
          const key = controlIdentityKey(control, lcombType);
          const existing = maxByControlKey.get(key);

          if (!existing || getUtilization(control) > getUtilization(existing.control)) {
            maxByControlKey.set(key, { control, lcombType });
          }
        }
      }
    }

    return Array.from(maxByControlKey.values());
  }, [lcombs, lcombGuidToType]);

  const maxControls =
    maxControlCandidatesFromLcombs.length > 0
      ? maxControlCandidatesFromLcombs.map((entry) => entry.control)
      : summaryMaxControls;

  const ulsMaxControls = sortControlsByConcreteType(
    maxControlCandidatesFromLcombs.length > 0
      ? maxControlCandidatesFromLcombs
          .filter((entry) => !isFireType(entry.lcombType) && !isSlsType(entry.lcombType))
          .map((entry) => entry.control)
      : maxControls.filter((ctrl) => {
          const type = lcNameToType.get(ctrl.lcName ?? "");
          return !isFireType(type) && !isSlsType(type);
        }),
  );
  const slsMaxControls =
    maxControlCandidatesFromLcombs.length > 0
      ? maxControlCandidatesFromLcombs.filter((entry) => isSlsType(entry.lcombType)).map((entry) => entry.control)
      : maxControls.filter((ctrl) => isSlsType(lcNameToType.get(ctrl.lcName ?? "")));
  const fireMaxControls =
    maxControlCandidatesFromLcombs.length > 0
      ? maxControlCandidatesFromLcombs.filter((entry) => isFireType(entry.lcombType)).map((entry) => entry.control)
      : maxControls.filter((ctrl) => isFireType(lcNameToType.get(ctrl.lcName ?? "")));
  const slsGroupedControls = React.useMemo<GroupedControlsItem[]>(() => {
    const groups = new Map<string, GroupedControlsItem>();

    for (const ctrl of slsMaxControls) {
      const fullTitle = ctrl.title ? t(`${ctrl.title}.label`) : "(untitled)";
      const { shortTitle, eurocodeRef } = splitControlTitle(fullTitle);
      const groupKey = shortTitle;
      const group = groups.get(groupKey);

      if (group) {
        group.controls.push(ctrl);
        if (!group.eurocodeRef && eurocodeRef) {
          group.eurocodeRef = eurocodeRef;
        }
      } else {
        groups.set(groupKey, {
          title: shortTitle,
          eurocodeRef,
          controls: [ctrl],
        });
      }
    }

    return Array.from(groups.values());
  }, [slsMaxControls, t]);
  const availableLcombGuids = new Set(lcombs.map((lc) => lc.lcombGuid).filter((guid): guid is string => Boolean(guid)));

  const availableLoadCombinationItems: NGDropdownListItem<string>[] = inputLoadCombinations
    .filter((lc) => {
      const guid = lc?.id?.guid;
      return Boolean(guid) && availableLcombGuids.has(guid!);
    })
    .map((lc) => ({
      name: lc.id?.name || lc.id?.guid || "",
      id: lc.id?.guid || "",
    }));

  React.useEffect(() => {
    if (availableLoadCombinationItems.length === 0 || !onSelLCChange) {
      return;
    }

    const selectedExists = availableLoadCombinationItems.some((item) => item.id === selLC);
    if (!selectedExists) {
      onSelLCChange(availableLoadCombinationItems[0].id);
    }
  }, [availableLoadCombinationItems, onSelLCChange, selLC]);

  const selectedLoadCombinationItem = availableLoadCombinationItems.find((item) => item.id === selLC) ?? null;
  const currentLcomb = selLC ? lcombs.find((lc) => lc.lcombGuid === selLC) : undefined;
  // The dropdown selects a single combination, so the whole list belongs to one limit state
  // (ULS or SLS) and can be sorted in one go. SLS control types are outside
  // CONCRETE_CONTROL_ORDER, so for an SLS combination the sort keeps the engine's order.
  const lcControls: ng.result.control.ControlData_json[] = sortControlsByConcreteType(
    (currentLcomb?.elementResults ?? []).flatMap((elRes) => {
      const result = elRes.result?.result;
      if (!result) return [];
      return [
        ...(result.rc?.maxOfControls ?? []),
        ...(result.steel?.maxOfControls ?? []),
        ...(result.timber?.maxOfControls ?? []),
      ];
    }),
  );

  const hasMaxTab = maxControls.length > 0;
  const hasLcTab = availableLoadCombinationItems.length > 0;

  React.useEffect(() => {
    const isCurrentTabValid = (activeTab === "max" && hasMaxTab) || (activeTab === "lc" && hasLcTab);

    if (isCurrentTabValid) {
      return;
    }

    if (hasMaxTab) {
      setActiveTab("max");
      return;
    }

    if (hasLcTab) {
      setActiveTab("lc");
    }
  }, [activeTab, hasLcTab, hasMaxTab]);

  // A fresh code check / design replaces the output object. When that happens,
  // snap back to the "max of all load combinations" overview — the most useful
  // view right after a code check. Switching to a specific load combination
  // afterwards is preserved, since that doesn't replace the output object.
  React.useEffect(() => {
    const currentOutput = selProject?.data?.data?.output;
    if (lastResultsRef.current === currentOutput) {
      return;
    }
    lastResultsRef.current = currentOutput;
    if (hasMaxTab) {
      setActiveTab("max");
    }
  }, [selProject?.data?.data?.output, hasMaxTab]);

  const hasAny = maxControls.length > 0 || lcControls.length > 0;

  if (!hasAny) {
    return <div className="rc-controls-empty-state">{t("no_code_check_controls_.label")}</div>;
  }

  return (
    <div className="rc-controls-report">
      <Tabs activeKey={activeTab} onSelect={(key) => setActiveTab(key ?? "max")} className="simplex-custom-tabs mb-2">
        {/* ── Max of load combinations ── */}
        {maxControls.length > 0 && (
          <Tab eventKey="max" title={t("results_max_of_load_combinations_.label")}>
            <div className="rc-controls-max-pane">
              {ulsMaxControls.length > 0 && (
                <div className="rc-controls-segment">
                  <div className="rc-controls-segment-header">{t("strength_uls_.label")}</div>
                  <ControlList
                    controls={ulsMaxControls}
                    expanded={expandedUlsControls}
                    setExpanded={setExpandedUlsControls}
                    t={t}
                  />
                </div>
              )}
              {slsGroupedControls.length > 0 && (
                <div className="rc-controls-segment">
                  <div className="rc-controls-segment-header">{t("servicability_sls_.label")}</div>
                  <GroupedControlList
                    groups={slsGroupedControls}
                    expanded={expandedSlsControls}
                    setExpanded={setExpandedSlsControls}
                  />
                </div>
              )}
              {fireMaxControls.length > 0 && (
                <div className="rc-controls-segment">
                  <div className="rc-controls-segment-header">{t("fire_.label")}</div>
                  <FireControlSummary
                    controls={fireMaxControls}
                    expanded={expandedFireControls}
                    setExpanded={setExpandedFireControls}
                    t={t}
                  />
                </div>
              )}
            </div>
          </Tab>
        )}

        {/* ── Current load combination ── */}
        {availableLoadCombinationItems.length > 0 && (
          <Tab eventKey="lc" title={t("results_current_load_combination_.label")}>
            <div className="rc-controls-lc-pane">
              <span>{t("load_combinations_.label")}</span>
              <div className="rc-controls-dropdown-wrap">
                <SXDropdown
                  id="lco-dropdown"
                  toggleId="lco_dropdown_temp_rc_controls"
                  className="sx-dropdown"
                  dataTestId="dropdown_lco_temp_rc_controls"
                  name="lco"
                  lang="en"
                  list={availableLoadCombinationItems}
                  val={selectedLoadCombinationItem}
                  onUpdate={(value) => value && onSelLCChange?.(value.id)}
                  rawNames
                  localisation={false}
                />
              </div>

              {lcControls.length > 0 ? (
                <ControlList
                  controls={lcControls}
                  expanded={expandedLcControls}
                  setExpanded={setExpandedLcControls}
                  t={t}
                />
              ) : (
                <div className="rc-controls-empty-inline">{t("no_controls_for_selected_load_combination_.label")}</div>
              )}
            </div>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared sub-component

export interface ControlListProps {
  controls: ng.result.control.ControlData_json[];
  expanded: Record<number, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  t: (key: string) => string;
}

interface GroupedControlListProps {
  groups: GroupedControlsItem[];
  expanded: Record<number, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
}

interface FireControlSummaryProps {
  controls: ng.result.control.ControlData_json[];
  expanded: Record<number, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  t: (key: string) => string;
}

const splitMathMl = (mathMlSrc: string) => {
  let mathMl = mathMlSrc.replace(/<mo>[ ]*=[ ]*<\/mo>/g, (match: string, offset: number, str: string) => {
    const mathStart = str.lastIndexOf("<math", offset);
    const mathEnd = str.indexOf(">", mathStart);
    const mathTag = str.slice(mathStart, mathEnd + 1);
    return `</math> ${mathTag}` + match;
  });

  return mathMl;
};

const MATHML_NS = "http://www.w3.org/1998/Math/MathML";

/**
 * Fence characters that a `<mo>` draws by GROWING to fit its neighbours, so only these are routed to the
 * math font (see the stretchy-glyph rules in SX-rc-control-accordion.scss). A `,` or `+` inside the same
 * group never stretches and stays in the report typeface.
 */
const STRETCHY_FENCE_GLYPHS = new Set(["(", ")", "[", "]", "{", "}", "|", "‖", "⌊", "⌋", "⌈", "⌉"]);

/**
 * Rewrites `<mfenced>` to the explicit `<mrow><mo>(</mo>…<mo>)</mo></mrow>` form.
 *
 * `mfenced` was dropped from MathML Core and **neither Chrome nor Edge implements it** — the element is
 * ignored and only its children are drawn, so the fences vanish silently. Measured in Chrome 153 and
 * Edge 153: `cot<mfenced>θ</mfenced>` renders as "cotθ", `<mfenced open='[' close=']'>100</mfenced>` as
 * "100", and a two-argument `min` as "minq1q" — the separator comma goes too, which makes that one
 * actively misleading rather than merely ugly. This is content loss, not a sizing problem.
 *
 * In the samples measured, only the RC/concrete engine emits `mfenced` (12 in one concrete column;
 * steel and timber use `<mo fence="true">` and had none) — but the expansion is not material-specific,
 * so do not assume steel and timber can never carry one. The generator is ours: see `MathmlCreator.cpp`
 * in WS_PRE_Stress, which writes both the bare `<mfenced>` default-paren form and
 * `<mfenced open='[' close=']'>`.
 *
 * Expands innermost-first so nested fences survive, and keeps non-element children: iterating
 * `el.children` instead of `childNodes` silently deleted them, turning `<mfenced open='[' close=']'>100`
 * into an empty `[]` and `<mfenced><mn>1</mn>+<mn>2</mn></mfenced>` into `(1,2)` — a different formula.
 *
 * The `separators` attribute is honoured (defaulting to "," per the spec) because the generator emits
 * genuinely multi-argument fences — `min(q, 1/q)` and `max(λy/λz, λz/λy)` — where dropping the comma
 * changes what the formula says. This is a TRADE against print's `expandMfenced`, not an improvement on
 * it: print splices the inner markup verbatim and so can never invent or drop a token, whereas a
 * separator inserted between the children of a *grouping* fence would read as an argument list
 * (`[100, ·, k]`). It is safe here only because the generator wraps every grouping fence's content in a
 * single `<mrow>`, leaving one child and therefore no separator. Drop an `<mrow>` there and this turns a
 * formula into a list — so if that invariant ever breaks, take print's verbatim approach instead.
 */
const expandMfenced = (root: ParentNode): void => {
  const makeOperator = (text: string): Element => {
    const mo = document.createElementNS(MATHML_NS, "mo");
    mo.textContent = text;
    return mo;
  };

  for (;;) {
    // Innermost = an <mfenced> with no <mfenced> left inside it.
    const innermost = Array.from(root.querySelectorAll("mfenced")).filter((el) => !el.querySelector("mfenced"));
    if (!innermost.length) break;

    innermost.forEach((el) => {
      const open = el.getAttribute("open") ?? "(";
      const close = el.getAttribute("close") ?? ")";
      // A missing attribute means ","; an explicitly empty one means no separators at all.
      const separators = (el.getAttribute("separators") ?? ",").replace(/\s+/g, "");
      const row = document.createElementNS(MATHML_NS, "mrow");

      if (open) row.appendChild(makeOperator(open));
      // childNodes, not children: a bare text child is content and must survive. Only element children
      // count as arguments for separator placement — a stray text node is not a new argument.
      const kept = Array.from(el.childNodes).filter(
        (node) => node.nodeType === Node.ELEMENT_NODE || (node.textContent ?? "").trim().length > 0,
      );
      let argIndex = 0;
      kept.forEach((child) => {
        if (child.nodeType === Node.ELEMENT_NODE) {
          // The last separator repeats if there are more arguments than separators (MathML spec).
          if (argIndex > 0 && separators.length) {
            row.appendChild(makeOperator(separators[Math.min(argIndex - 1, separators.length - 1)]));
          }
          argIndex += 1;
        }
        row.appendChild(child);
      });
      if (close) row.appendChild(makeOperator(close));

      el.replaceWith(row);
    });
  }
};

/**
 * The constructors tall enough that a fence around them is actually stretched, and the gate on
 * `symmetric="false"`.
 *
 * A stretchy fence is sized symmetrically about the math axis by default, so it grows by the LARGER of
 * its two halves in BOTH directions. Around a fraction with a one-character numerator and a tall
 * denominator that leaves it towering over the numerator: on the flexural-buckling line
 * `chi = min(1 / (phi + sqrt(phi^2 - lambda^2)), 1.0)` the paren measured 59px around a 36.4px fraction
 * in Chrome and 53px in Edge — 20.1px of it above the fraction against 2.6px below. `symmetric="false"`
 * sizes it from the content's own ascent and descent instead: 37px in both engines, 0.3px proud top and
 * bottom, so the lower wing is drawn longer than the upper one, which is what the expression actually
 * looks like. Both engines honour the attribute, and it is an attribute, not a CSS property — hence
 * setting it here rather than in the stylesheet with the rest of the fence styling.
 *
 * Gated rather than set on every fence, because it also re-centres the fences that were NEVER stretched
 * onto their content box instead of the axis: an ordinary `(0.58 - 0.2)` keeps its 17px parens but they
 * rise 2.2px (measured, both engines) and stop hanging below the baseline, the way a paren around plain
 * numbers is supposed to. Where the group holds nothing tall there is nothing to fix.
 */
const TALL_CONSTRUCTORS = "mfrac, msqrt, mroot, munder, mover, munderover";

/**
 * Marks every fence `<mo>` with `rc-stretchy` so the stylesheet can draw just those glyphs from the math
 * font. CSS cannot match on an element's text, hence the tagging pass.
 *
 * The steel and timber payloads mark their fences with `fence="true"` and could be selected on the
 * attribute alone, but the concrete payload does not: it carries 12 bare `<mo>(</mo>` and not one
 * `fence` attribute. Tagging on the character covers all three, and the fences that `expandMfenced` has
 * just created — which is why this runs after it.
 *
 * A fence whose parent is the `<math>` root is deliberately NOT tagged. A stretchy operator grows to its
 * enclosing row, so one sitting at the root of a row that also holds an `<mfrac>` grows to the FRACTION,
 * not to the sub-expression it encloses: concrete's `(cot θ + cot α)` measured 39px around 13px of
 * content once drawn from the math font, against 13px for the same parens correctly nested in an
 * `<mrow>`. The generator writes those parens as bare siblings of the fraction (the V_Rd,s formula in
 * `MathmlCreator.cpp`) with no `<mrow>` marking the group, so nothing in the markup says how far the
 * fence is meant to reach. Leaving them untagged keeps them at base size — what they were before any of
 * this — rather than confidently the wrong size. Fixing them properly means inferring the group and
 * wrapping it in an `<mrow>`, which is a guess about intent, not a rendering fix.
 *
 * Tagged fences are also sized from their content rather than about the math axis — see
 * `TALL_CONSTRUCTORS`.
 */
const tagStretchyFences = (root: ParentNode): void => {
  root.querySelectorAll("mo").forEach((mo) => {
    const text = (mo.textContent ?? "").replace(/\u00A0/g, " ").trim();
    if (!STRETCHY_FENCE_GLYPHS.has(text)) return;
    if (mo.parentElement?.tagName.toLowerCase() === "math") return;
    mo.classList.add("rc-stretchy");
    if (mo.parentElement?.querySelector(TALL_CONSTRUCTORS)) mo.setAttribute("symmetric", "false");
  });
};

/**
 * Removes the stray slash row the steel engine (fem-design-api) puts between a fraction's numerator and
 * denominator: `<mfrac><mrow>a</mrow><mrow>/</mrow><mrow>b</mrow></mfrac>`. A three-child `<mfrac>` is
 * invalid, and Chromium lays it out as a plain row with the bare "/" dropped, so `A·f_y / (√3·γ_M0)` reads
 * as "A·f_y√3·γ_M0" — a wrong-looking formula rather than an error. Dropping the row restores a two-child
 * fraction. A real division operator is written `<mo>/</mo>` and is untouched.
 *
 * Stopgap until the engine emits a valid `<mfrac>`; a no-op for well-formed formulas. sx-center's print
 * module (`dropFractionSlashRow`) and sx-beam carry the same workaround.
 */
const dropFractionSlashRow = (root: ParentNode): void => {
  root.querySelectorAll("mfrac > mrow").forEach((mrow) => {
    if (mrow.childElementCount === 0 && (mrow.textContent ?? "").trim() === "/") mrow.remove();
  });
};

const sanitizeMathmlIdentifierSpacing = (mathml: string): string => {
  if (!mathml) return "";

  const container = document.createElement("div");
  container.innerHTML = mathml;

  dropFractionSlashRow(container);

  // Before anything else: <mfenced> is not implemented by any current browser, so its fences are simply
  // not drawn. Rewriting it to explicit <mo> fences is what makes them visible at all — and it has to
  // run before tagStretchyFences, which styles the operators this produces.
  expandMfenced(container);

  // Some payloads inject whitespace-only <mo> inside <mi>, which shifts identifier baselines.
  container.querySelectorAll("mi > mo").forEach((mo) => {
    const decodedText = (mo.textContent ?? "").replace(/\u00A0/g, " ").trim();
    const inner = mo.innerHTML.replace(/\s+/g, "").toLowerCase();
    const isWhitespaceOperator =
      decodedText.length === 0 ||
      inner === "&nbsp;" ||
      inner === "&amp;nbsp;" ||
      inner === "&#160;" ||
      inner === "&#xa0;";

    if (isWhitespaceOperator) {
      mo.remove();
    }
  });

  // Fix: <mi> wrapping other MathML elements (e.g. <mi><mi>cot</mi><mfenced>…</mfenced></mi>)
  // causes stacking/baseline issues. Replace such outer <mi> with <mrow>.
  container.querySelectorAll("mi").forEach((mi) => {
    const hasMathmlChildren = mi.querySelector("mi, mo, mn, mfenced, mrow, msup, msub, mfrac, msqrt");
    if (hasMathmlChildren) {
      const mrow = document.createElement("mrow");
      while (mi.firstChild) {
        mrow.appendChild(mi.firstChild);
      }
      mi.replaceWith(mrow);
    }
  });

  // Force default app text color by removing explicit color declarations from payload.
  container.querySelectorAll("*").forEach((node) => {
    const styleAttr = node.getAttribute("style");
    if (styleAttr) {
      const cleanedStyle = styleAttr
        .split(";")
        .map((decl) => decl.trim())
        .filter((decl) => {
          const lower = decl.toLowerCase();
          return (
            lower.length > 0 &&
            !lower.startsWith("color:") &&
            !lower.startsWith("mathcolor:") &&
            !lower.startsWith("fill:") &&
            !lower.startsWith("stroke:")
          );
        })
        .join("; ");

      if (cleanedStyle) {
        node.setAttribute("style", cleanedStyle);
      } else {
        node.removeAttribute("style");
      }
    }

    node.removeAttribute("color");
    node.removeAttribute("mathcolor");
  });

  // Chromium renders <mfrac> in the default (text) style, which shrinks the numerator/denominator to
  // script size. A stretchy <mo> (parenthesis) or <msqrt> wrapping such a fraction then stretches to
  // match that shrunk height, so it reads as undersized next to the rest of the (normal-size) formula
  // whenever a division is present. Forcing every <math> root into display style renders fractions —
  // and anything stretchy around them — at full size. Mirrors the fix already applied in sx-center's
  // print module (see withDisplayStyle in print/transformers/shared-utils.ts).
  container.querySelectorAll("math:not([displaystyle])").forEach((math) => {
    math.setAttribute("displaystyle", "true");
  });

  // Last, so it also catches the fences expandMfenced created above.
  tagStretchyFences(container);

  return container.innerHTML;
};

/**
 * A trailing Eurocode equation reference — "(6.18)", "(6.2)", "(6.2.a)", and the comma-separated form
 * "(6.13), (6.14)" that a check derived from two clauses carries. Matching only a single clause left
 * that line's reference inline while every neighbouring row had one in the right-hand column.
 */
const ONE_CLAUSE = String.raw`\(\s*\d+(?:\.[0-9a-z]+)*\s*\)`;
// Concatenation, not a template literal: in a template literal `\s` collapses to `s`, which
// silently turned the separator into a literal "s" and left multi-clause refs unmatched.
const EQUATION_REF = new RegExp("^" + ONE_CLAUSE + "(?:\\s*,\\s*" + ONE_CLAUSE + ")*$", "i");

/**
 * Pulls each derivation line's trailing equation reference out to the right-hand edge, so the refs line
 * up in a column instead of trailing the expression where they are lost among the numbers.
 *
 * The payload is one blob of `<br />`-separated lines (see the `print_math_jax` wrapper the backend
 * emits), so there is nothing per-line to align against. This groups the nodes between the breaks into
 * `.rc-formula-line` rows — which is also what lets the reference be positioned at all — and moves a
 * trailing `<span>(6.18)</span>` into its own `.rc-formula-eqref` cell.
 *
 * Mirrors `extractEquationRef` + `alignEqRefs` in sx-center's print module, but matches on the DOM
 * rather than the string: the reference is a real `<span>` sibling here (the "steel/timber form" that
 * function's first branch handles), so no regex over the markup is needed. A line whose last span is a
 * verdict suffix (`(6.25)</span><span> - OK</span>`) still aligns — the LAST ref-shaped span wins and
 * the suffix is left where it is.
 *
 * A no-op for payloads with no `<br />` or no references, so it is safe on every control.
 */
const alignEquationRefs = (html: string): string => {
  if (!html) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  // The backend wraps the derivation in `div.print_math_jax`; fall back to the root for other shapes.
  const host = container.querySelector("div.print_math_jax") ?? container;
  const children = Array.from(host.childNodes);
  if (!children.some((n) => n.nodeName.toLowerCase() === "br")) return html;

  // Group the nodes between <br /> into lines; the breaks themselves are dropped, since the
  // .rc-formula-line rows that replace them are blocks and would otherwise double the leading.
  const lines: Node[][] = [[]];
  children.forEach((node) => {
    if (node.nodeName.toLowerCase() === "br") lines.push([]);
    else lines[lines.length - 1].push(node);
  });

  const rows = lines.map((nodes) => {
    const row = document.createElement("div");
    row.className = "rc-formula-line";

    const body = document.createElement("span");
    body.className = "rc-formula-body";
    nodes.forEach((node) => body.appendChild(node));

    // The payloads carry the reference in THREE different shapes — the same set print's
    // extractEquationRef handles across its branches. Matching only some of them looks like the feature
    // working, because the materials that use the common shape come out right.
    const makeRef = (text: string): Element => {
      const span = document.createElement("span");
      span.className = "rc-formula-eqref";
      span.textContent = text;
      return span;
    };

    // (a) Steel and timber: a trailing <span>(6.18)</span> element. Searching from the end keeps the
    //     verdict suffix ("- OK") in the body, where it reads as part of the result.
    let ref: Element | null =
      Array.from(body.querySelectorAll(":scope > span"))
        .reverse()
        .find((span) => EQUATION_REF.test((span.textContent ?? "").trim())) ?? null;

    if (ref) {
      ref.remove();
      ref.className = `${ref.className} rc-formula-eqref`.trim();
    } else {
      // (b) Concrete: bare text after the last </math>, padded right with em-spaces — "&emsp;(6.13)".
      const last = body.lastChild;
      const text = last?.nodeType === Node.TEXT_NODE ? (last.textContent ?? "") : "";
      const match = text.match(new RegExp(`^([\\s\\S]*?)[\\s\u00A0\u2003]*(${ONE_CLAUSE})[\\s\u00A0\u2003]*$`, "i"));
      if (last && match) {
        // Drop the padding with the reference; it exists only to push it right by hand.
        last.textContent = match[1];
        ref = makeRef(match[2]);
      } else {
        // (c) Concrete again, for the clauses the generator numbers itself (V_Rd,c "(6.2.a)"): a
        //     standalone trailing <math> whose only visible content IS the reference. The ref is an
        //     element inside its own <math>, so neither branch above sees it.
        const lastMath = body.querySelector(":scope > math:last-child");
        const mathText = (lastMath?.textContent ?? "").replace(/[\s\u00A0\u2003]+/g, "");
        if (lastMath && EQUATION_REF.test(mathText)) {
          lastMath.remove();
          ref = makeRef(mathText);
        }
      }
    }

    if (ref) {
      // The backend pads a line's end with runs of `<mo>&#xa0;&#xa0;&#xa0;&#xa0;</mo>` to shove the
      // reference towards the right margin by hand. Now that the reference is positioned properly that
      // padding only leaves a gap mid-line — most visibly stranding the "- OK" verdict that follows the
      // reference in the payload. Only TRAILING spacers inside each <math> go; the identical runs used
      // BETWEEN terms (e.g. between "Class_N = 2" and "Class_M1 = 1") are real column spacing and stay.
      //
      // Only when a reference was actually extracted. Stripping it unconditionally also flattened lines
      // whose trailing span is not a reference at all — "(Buckling curve: a)", or the two-clause
      // "(6.13), (6.14)" before that shape was matched — which lost their lead-in gap without gaining
      // the right-hand alignment that was the point of removing it.
      body.querySelectorAll("math").forEach((math) => {
        let last = math.lastElementChild;
        while (
          last &&
          last.tagName.toLowerCase() === "mo" &&
          !(last.textContent ?? "").replace(/\u00A0/g, " ").trim()
        ) {
          last.remove();
          last = math.lastElementChild;
        }
      });
    }

    row.appendChild(body);
    if (ref) row.appendChild(ref);
    return row;
  });

  // A group with no content came from a <br /><br /> in the payload, which is how it separates sections
  // — the blank line before "At design load:" or before the closing utilisation. Those are deliberate
  // and must survive as blank rows, or the derivation runs together into an unreadable block. Only
  // trailing blanks go: they are the artefact of the payload ending with a <br />.
  const isBlank = (row: Element) => !(row.textContent ?? "").trim() && !row.querySelector("math");
  while (rows.length && isBlank(rows[rows.length - 1])) rows.pop();
  rows.forEach((row) => {
    if (isBlank(row)) row.classList.add("rc-formula-line--blank");
  });

  if (!rows.length) return html;

  host.replaceChildren(...rows);
  return container.innerHTML;
};

const splitControlTitle = (title: string): { shortTitle: string; eurocodeRef: string | null } => {
  const delimiter = " - ";
  const splitIndex = title.indexOf(delimiter);

  if (splitIndex === -1) {
    return { shortTitle: title, eurocodeRef: null };
  }

  const trailingPart = title.slice(splitIndex + delimiter.length).trim();
  // Concrete refs are untranslated Eurocode designations ("EN 1992-1-1: ...") or bare clause
  // numbers ("6.1.4, 6.2.4"), so they always start with a digit. Steel/timber refs are of the
  // form "Part 1-1: 6.3.1", but "Part" is localized ("Del" in Swedish/Danish, "Osa" in Finnish),
  // so matching that literal English word only worked for the English locale. Instead, accept any
  // single leading word followed by a digit, which covers all of the above regardless of locale.
  const looksLikeEurocodeRef = /^(\d|\S+\s+\d)/.test(trailingPart);

  if (!looksLikeEurocodeRef) {
    return { shortTitle: title, eurocodeRef: null };
  }

  return {
    shortTitle: title.slice(0, splitIndex),
    eurocodeRef: trailingPart,
  };
};

const getControlDisplayTitle = (control: ng.result.control.ControlData_json, t: (key: string) => string) => {
  const fullTitle = control.title ? t(`${control.title}.label`) : "(untitled)";
  return splitControlTitle(fullTitle);
};

export const getUtilizationValue = (control: ng.result.control.ControlData_json): number | null => {
  if (control.utilization == null) {
    return null;
  }

  const value = Number(control.utilization);
  return Number.isFinite(value) ? value : null;
};

const getUtilizationPercent = (control: ng.result.control.ControlData_json): string => {
  const value = getUtilizationValue(control);
  return value != null ? (value * 100).toFixed(1) : "—";
};

export const getMaxUtilizationPercent = (controls: ng.result.control.ControlData_json[]): string => {
  const maxUtilization = controls.reduce<number | null>((maxValue, control) => {
    const value = getUtilizationValue(control);

    if (value == null) {
      return maxValue;
    }

    return maxValue == null ? value : Math.max(maxValue, value);
  }, null);

  return maxUtilization != null ? (maxUtilization * 100).toFixed(1) : "—";
};

const isOverUtilized = (controls: ng.result.control.ControlData_json[]): boolean =>
  controls.some((control) => {
    const value = getUtilizationValue(control);
    return value != null && value > 1;
  });

const getShortMathmlMarkup = (control: ng.result.control.ControlData_json): string => {
  const fullMathml = alignEquationRefs(splitMathMl(sanitizeMathmlIdentifierSpacing(control.mathml ?? "")));
  const shortMathml = sanitizeMathmlIdentifierSpacing(control.mathmlShort ?? "");
  return shortMathml || fullMathml;
};

const getExpandedMathmlMarkup = (control: ng.result.control.ControlData_json): string => {
  const fullMathml = alignEquationRefs(splitMathMl(sanitizeMathmlIdentifierSpacing(control.mathml ?? "")));
  const shortMathml = sanitizeMathmlIdentifierSpacing(control.mathmlShort ?? "");
  return fullMathml || shortMathml || "";
};

// The order engineers expect to read the concrete controls in: axial force first, then the
// moments (M1, M2, biaxial), then shear. The engine reports either the split positive/negative
// moment controls or the merged "max of both" control, and shear either as a force or as a
// stirrup check, so all spellings are listed. Types not listed here (SLS, fire, torsion,
// steel/timber controls, …) keep the order they arrive in.
const CONCRETE_CONTROL_ORDER: ng.result.control.ControlTypeConcrete[] = [
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_AXIAL_FORCE,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_MOMENT_M2,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_POSITIVE_MOMENT_M2,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_NEGATIVE_MOMENT_M2,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_MOMENT_M1,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_POSITIVE_MOMENT_M1,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_NEGATIVE_MOMENT_M1,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_BIAXIAL_MOMENT,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_FORCE_2,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_STIRRUPS_2,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_FORCE,
  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_SHEAR_STIRRUPS,
];

const concreteControlRanks = new Map<ng.result.control.ControlTypeConcrete, number>(
  CONCRETE_CONTROL_ORDER.map((type, index) => [type, index]),
);

const getConcreteControlRank = (control: ng.result.control.ControlData_json): number => {
  if (control.concreteType == null) {
    return Number.MAX_SAFE_INTEGER;
  }

  const type = ng.result.control.ControlTypeConcrete_FromObject(control.concreteType);
  return concreteControlRanks.get(type) ?? Number.MAX_SAFE_INTEGER;
};

/**
 * Sorts controls into CONCRETE_CONTROL_ORDER. The sort is stable, so controls of the same
 * type — and every type outside the list — keep their incoming relative order.
 */
export const sortControlsByConcreteType = (
  controls: ng.result.control.ControlData_json[],
): ng.result.control.ControlData_json[] =>
  [...controls].sort((a, b) => getConcreteControlRank(a) - getConcreteControlRank(b));

export const ControlList: React.FC<ControlListProps> = ({ controls, expanded, setExpanded, t }) => (
  <>
    {controls.map((ctrl, idx) => {
      const utilPct = getUtilizationPercent(ctrl);
      const isOver = isOverUtilized([ctrl]);
      const fullMathml = getExpandedMathmlMarkup(ctrl);
      const shortMathml = getShortMathmlMarkup(ctrl);

      const hasFullMathml = fullMathml.length > 0;
      const showFull = Boolean(expanded[idx]);

      const { shortTitle, eurocodeRef } = getControlDisplayTitle(ctrl, t);
      const eventKey = "open";

      return (
        <Accordion
          key={idx}
          activeKey={showFull ? eventKey : undefined}
          onSelect={() => setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }))}
          className="rc-control-accordion"
        >
          <Accordion.Item
            eventKey={eventKey}
            style={{
              ["--rc-control-border" as string]: isOver ? "#dc3545" : "#28a745",
            }}
          >
            <Accordion.Header>
              <div className="rc-control-header-content">
                <div className="rc-control-title-row">
                  <div>
                    <strong className="rc-control-title">{shortTitle}</strong>
                    {eurocodeRef && showFull && (
                      <>
                        <br />
                        <span className="rc-control-eurocode-ref">{eurocodeRef}</span>
                      </>
                    )}
                  </div>

                  <div className="rc-control-meta-row">
                    <span>LC: {ctrl.lcName || "—"}</span>
                    <span className="rc-control-meta-x">
                      x:{" "}
                      {ctrl.x != null ? (
                        <>
                          {Number(ctrl.x).toFixed(3)}
                          <span style={{ textTransform: "none" }}> m</span>
                        </>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  <span className="rc-control-utilization">{utilPct}%</span>
                </div>

                {/* Keep short formula visible in collapsed state */}
                {!showFull && (ctrl.mathmlShort || hasFullMathml) && (
                  <div className="rc-control-short-mathml-wrap">
                    <div
                      className="rc-control-short-mathml"
                      dangerouslySetInnerHTML={{ __html: shortMathml || fullMathml }}
                    />
                  </div>
                )}
              </div>
            </Accordion.Header>

            <Accordion.Body
              style={{
                backgroundColor: "#f9f9f9",
                padding: "12px 16px",
                borderRadius: "4px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                className={`rc-mathml-fade${showFull ? " rc-mathml-fade-in" : ""}`}
                dangerouslySetInnerHTML={{ __html: fullMathml || shortMathml || "" }}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      );
    })}
  </>
);

const GroupedControlList: React.FC<GroupedControlListProps> = ({ groups, expanded, setExpanded }) => (
  <>
    {groups.map((group, idx) => {
      const showFull = Boolean(expanded[idx]);
      const eventKey = "open";
      const isOver = isOverUtilized(group.controls);
      const maxUtilization = getMaxUtilizationPercent(group.controls);

      return (
        <Accordion
          key={`${group.title}-${idx}`}
          activeKey={showFull ? eventKey : undefined}
          onSelect={() => setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }))}
          className="rc-control-accordion"
        >
          <Accordion.Item
            eventKey={eventKey}
            style={{
              ["--rc-control-border" as string]: isOver ? "#dc3545" : "#28a745",
            }}
          >
            <Accordion.Header>
              <div className="rc-control-header-content">
                <div className="rc-control-title-row">
                  <div>
                    <strong className="rc-control-title">{group.title}</strong>
                    {group.eurocodeRef && showFull && (
                      <>
                        <br />
                        <span className="rc-control-eurocode-ref">{group.eurocodeRef}</span>
                      </>
                    )}
                  </div>
                  <span className="rc-control-utilization">{maxUtilization}%</span>
                </div>
              </div>
            </Accordion.Header>

            <Accordion.Body className="rc-control-group-body">
              {group.controls.map((control, controlIdx) => {
                const shortMathml = getShortMathmlMarkup(control);
                return (
                  <div key={`${group.title}-${controlIdx}`} className="rc-control-group-entry">
                    <div className="rc-control-group-entry-meta">
                      <span>LC: {control.lcName || "—"}</span>
                      <span className="rc-control-meta-x">
                        x: {control.x != null ? `${Number(control.x).toFixed(3)} m` : "—"}
                      </span>
                      <span className="rc-control-group-entry-utilization">{getUtilizationPercent(control)}%</span>
                    </div>
                    {shortMathml && (
                      <div
                        className="rc-control-short-mathml rc-control-group-mathml"
                        dangerouslySetInnerHTML={{ __html: shortMathml }}
                      />
                    )}
                  </div>
                );
              })}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      );
    })}
  </>
);

const FireControlSummary: React.FC<FireControlSummaryProps> = ({ controls, expanded, setExpanded, t }) => {
  const showFull = Boolean(expanded[0]);
  const eventKey = "open";
  const isOver = isOverUtilized(controls);
  const maxUtilization = getMaxUtilizationPercent(controls);

  return (
    <Accordion
      activeKey={showFull ? eventKey : undefined}
      onSelect={() => setExpanded((prev) => ({ ...prev, 0: !prev[0] }))}
      className="rc-control-accordion"
    >
      <Accordion.Item
        eventKey={eventKey}
        style={{
          ["--rc-control-border" as string]: isOver ? "#dc3545" : "#28a745",
        }}
      >
        <Accordion.Header>
          <div className="rc-control-header-content">
            <div className="rc-control-title-row">
              <strong className="rc-control-title">{t("all_fire_checks_.label")}</strong>
              <span className="rc-control-utilization">{maxUtilization}%</span>
            </div>
          </div>
        </Accordion.Header>

        <Accordion.Body className="rc-control-group-body">
          {controls.map((control, idx) => {
            const shortMathml = getShortMathmlMarkup(control);
            const { shortTitle, eurocodeRef } = getControlDisplayTitle(control, t);

            return (
              <div key={`fire-${idx}`} className="rc-control-group-entry rc-control-fire-entry">
                <div className="rc-control-group-entry-meta rc-control-fire-entry-meta">
                  <div>
                    <strong className="rc-control-title">{shortTitle}</strong>
                    {eurocodeRef && <div className="rc-control-eurocode-ref">{eurocodeRef}</div>}
                  </div>
                  <span>LC: {control.lcName || "—"}</span>
                  <span className="rc-control-meta-x">
                    x: {control.x != null ? `${Number(control.x).toFixed(3)} m` : "—"}
                  </span>
                  <span className="rc-control-group-entry-utilization">{getUtilizationPercent(control)}%</span>
                </div>
                {shortMathml && (
                  <div
                    className="rc-control-short-mathml rc-control-group-mathml"
                    dangerouslySetInnerHTML={{ __html: shortMathml }}
                  />
                )}
              </div>
            );
          })}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};
